import { geminiDirectionStyle, type GeminiDirection } from "../../supabase/functions/_shared/gemini-directions";
import { actualVolume, type AudioVolumeDirection } from './audioVolume';
import {actualSpeed,type AudioSpeedDirection} from './audioSpeed';

// Authoring metadata only. Never pass timing or unspoken text to a TTS provider.
export const DELIVERY_STRENGTHS = ["Subtle", "Light", "Clear", "Strong", "Pronounced"] as const;
export type DeliveryStrength = typeof DELIVERY_STRENGTHS[number];
export type EditorGeminiDirection = GeminiDirection & AudioVolumeDirection & AudioSpeedDirection & { deliveryStrength?: DeliveryStrength; pauseMs?: number; unspoken?: boolean; pauseMarker?: boolean; cueMarker?: boolean };
/** Flatten editor metadata into the existing validated style contract. Unedited legacy styles/cache keys stay identical. */
export function editorDirectionStyle(direction: EditorGeminiDirection): string {
  const strength = direction.deliveryStrength;
  if (!direction.delivery || !strength || !DELIVERY_STRENGTHS.includes(strength)) return geminiDirectionStyle(direction);
  const expression = { Subtle: "very subtly", Light: "lightly", Clear: "clearly", Strong: "strongly", Pronounced: "very prominently" }[strength];
  return geminiDirectionStyle({ ...direction, delivery: `${direction.delivery} delivery expressed ${expression}` });
}
export const isPauseCue = (cue?: string) => cue === "short pause" || cue === "long pause";
export function pauseMilliseconds(direction?: EditorGeminiDirection): number {
  if (direction?.pauseMs !== undefined) {
    if (!Number.isInteger(direction.pauseMs) || direction.pauseMs < 0 || direction.pauseMs > 10000) {
      throw new Error("Choose a pause between 0 and 10 seconds.");
    }
    return direction.pauseMs;
  }
  return direction?.cue === "short pause" ? 500 : direction?.cue === "long pause" ? 2000 : 0;
}
export function normalizePauseDirection(direction: EditorGeminiDirection): EditorGeminiDirection {
  return isPauseCue(direction.cue) ? { ...direction, pauseMs: pauseMilliseconds(direction), cue: undefined } : direction;
}
export function hasDeliveryDirection(direction: EditorGeminiDirection) {
  return Boolean(editorDirectionStyle(direction) || direction.cue || pauseMilliseconds(direction) || direction.unspoken || actualVolume(direction)||actualSpeed(direction));
}
export function describeDeliveryDirection(direction: EditorGeminiDirection) {
  return [editorDirectionStyle(direction), !isPauseCue(direction.cue) && direction.cue,
    direction.audioGainDb!==undefined?`Volume ${direction.audioGainDb>0?'+':''}${direction.audioGainDb} dB`:'',direction.volumeMotion?`${direction.volumeMotion} volume`:'',
    actualSpeed(direction)?`Recording speed ${direction.audioSpeed!.toFixed(2)}× (pitch preserved)`:'',
    pauseMilliseconds(direction) ? `Pause ${pauseMilliseconds(direction) / 1000}s` : "",
    direction.unspoken ? "Not spoken" : ""].filter(Boolean).join(" · ");
}
